import React, { useRef, useState } from 'react';
import Popover from './tasks/Popover';
import TaskIcon from './tasks/TaskIcon';
import { USER_COLORS, isSolidColor, noteColor } from '../utils/userColors';
import '../tasks.css';

/**
 * Admin › Users colour. Each choice is a pair, shown as a split swatch: the
 * strong half is the person's bubble on tasks (white initials), the light
 * half is the background of their notes on orders (black text).
 */
const UserColorPicker: React.FC<{ value: string | undefined; label: string; onChange: (color: string) => void }> = ({
  value,
  label,
  onChange,
}) => {
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState<string | null>(null);
  const anchor = useRef<HTMLButtonElement>(null);
  const current = USER_COLORS.find((c) => c.value.toLowerCase() === (value || '').toLowerCase());
  const picked = !!value && isSolidColor(value);
  const previewColor = hovered || (picked ? value! : null);
  const initial = (label || '?').trim().charAt(0).toUpperCase();

  const split = (color: string) => ({
    background: `linear-gradient(180deg, ${color} 0 50%, ${noteColor(color)} 50% 100%)`,
  });

  return (
    <>
      <button
        ref={anchor}
        type="button"
        className={`ucp-trigger${picked ? '' : ' is-unset'}`}
        onClick={() => setOpen(true)}
        title={current ? `${current.name} — bubble and note colours` : picked ? value : 'Pick a colour'}
        aria-label={`Colour for ${label}: ${current ? current.name : 'not set'}`}
      >
        <span className="ucp-swatch" style={picked ? split(value!) : undefined} />
        <TaskIcon name="chevron-down" size={12} />
      </button>
      {open && (
        <Popover anchor={anchor.current} onClose={() => setOpen(false)} label="Pick a colour" width={228} align="start">
          <div className="ucp-grid" role="radiogroup" aria-label="Colour" onMouseLeave={() => setHovered(null)}>
            {USER_COLORS.map((c) => {
              const selected = c.value.toLowerCase() === (value || '').toLowerCase();
              return (
                <button
                  key={c.value}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  className={`ucp-option${selected ? ' is-selected' : ''}`}
                  style={split(c.value)}
                  title={c.name}
                  aria-label={c.name}
                  onMouseEnter={() => setHovered(c.value)}
                  onFocus={() => setHovered(c.value)}
                  onClick={() => {
                    onChange(c.value);
                    setOpen(false);
                  }}
                >
                  {selected && <TaskIcon name="check" size={13} />}
                </button>
              );
            })}
          </div>
          <div className="ucp-preview" aria-hidden="true">
            {previewColor ? (
              <>
                <span className="ucp-preview-bubble" style={{ background: previewColor }}>{initial}</span>
                <span className="ucp-preview-label">Tasks</span>
                <span className="ucp-preview-note" style={{ background: noteColor(previewColor) }}>Order note</span>
              </>
            ) : (
              <span className="ucp-preview-label">Top half: task bubble · bottom half: order notes</span>
            )}
          </div>
        </Popover>
      )}
    </>
  );
};

export default UserColorPicker;
